/**
 * Button initializations
 */
import {isUserPremium} from '@Libraries/user.library';
import {redirectUser} from '@Utils/browser.util';
import {EmailCampaignSendingOptionsPanelTypes} from '@Panels/email-campaign-sending-options-panel/email-campaign-sending-options-panel.types';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src/index';
import type {EmailPreviewResponse} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {getEmailMarketingCampaignById, shareEmailMarketingCampaignInTeam, trackGA4NumberOfSectionsInEmailCampaign} from '@Libraries/email-campaigns-library';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {CollaborationGA4Source, CollaborationGA4Toggle, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {noop} from "@Utils/general.util";

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const bd = $('body');

  const GA_LABEL_EXPORT_HTML = 'Editor';
  const UPSELL_DIALOG_SHOWN_COOKIE = 'upsell-dialog-shown';
  const UPSELL_DIALOG_SHOWN_COOKIE_EXPIRATION_MINUTES = 30;
  const BUTTON_STATE_DEFAULT = 'default';
  const BUTTON_STATE_PROCESSING = 'processing';
  const BUTTON_STATE_SUCCESS = 'success';

  const showFitToScreenButton = (): void => {
    const fitToScreenExists = $('.show-fit-to-screen').length > 0;
    if (!fitToScreenExists) {
      $('.bottom-floating-poster-opts').addClass('show-fit-to-screen');
    }
  };

  const showZoomLabelPreview = (): void => {
    $('.bottom-floating-poster-opts').addClass('show-zoom-label');
    setTimeout(() => {
      $('.bottom-floating-poster-opts').removeClass('show-zoom-label');
    }, 2500);
  };

  /**
   * Changes the state of the export HTML button.
   *
   * @param {string} selectorPrefix
   * @param {string} newState
   */
  const changeButtonState = (selectorPrefix: string, newState: string): void => {
    const states = [BUTTON_STATE_DEFAULT, BUTTON_STATE_PROCESSING, BUTTON_STATE_SUCCESS];

    if (!states.includes(newState)) {
      return;
    }

    const statesToDisable = states.filter((state) => {
      return state !== newState;
    });

    for (let i = 0; i < statesToDisable.length; i++) {
      const selector = createButtonStateSelector(selectorPrefix, statesToDisable[i]);
      $(selector).addClass('_hidden');
    }

    const selector = createButtonStateSelector(selectorPrefix, newState);
    $(selector).removeClass('_hidden');
  };

  /**
   * Displays the premium upsell dialog if the user is not premium and the dialog has not been shown recently.
   */
  const displayPremiumUpsellDialog = (): void => {
    const isPremium = isUserPremium();
    if (isPremium) {
      return;
    }

    const dialogShown = window.PMW.util.readCookie(UPSELL_DIALOG_SHOWN_COOKIE) as string;
    if (dialogShown === '1') {
      return;
    }

    const expirationDays = UPSELL_DIALOG_SHOWN_COOKIE_EXPIRATION_MINUTES / (24 * 60);
    window.PMW.util.setCookie(UPSELL_DIALOG_SHOWN_COOKIE, '1', expirationDays);

    window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EMAIL_TEMPLATE_DOWNLOAD);
  };

  const hideSaveConflictPopup = (): void => {
    $('.js-save-conflict-popup').removeClass('-visible');
  };

  /**
   * Adds listeners to the editor to close the export HTML dropdown when the user clicks outside of it.
   */
  const addForceCloseDropdownListener = (): void => {
    const closeDropdown = (): void => {
      doKeepDropdownVisible(false);
    };

    editor.on('load', () => {
      $(editor.Canvas.getBody()).on(window.PMW.click, closeDropdown);
    });

    addClickOutsideListener($('.js-export-dropdown-list'), closeDropdown);
    addClickOutsideListener($('.js-save-conflict-popup'), hideSaveConflictPopup);
  };

  const getEmailHTMLPromise = async (): Promise<EmailPreviewResponse> => {
    return (await editor.runCommand(window.PMW.emails.commands.GET_EMAIL_HTML_PROMISE)) as EmailPreviewResponse;
  };

  /**
   * Adds a listener to an element when the user clicks outside of it.
   *
   * @param {JQuery} element
   * @param {function} callback
   */
  const addClickOutsideListener = (element: JQuery, callback: VoidFunction): void => {
    $(document).on(window.PMW.click, (event: MouseEvent) => {
      const target = event.target as unknown as HTMLElement;
      if (!element.is(target) && element.has(target).length === 0) {
        callback();
      }
    });
  };

  const trackGA4SectionsUsed = (): void => {
    if (window.PMW.idEmailCampaign) {
      trackGA4NumberOfSectionsInEmailCampaign(window.PMW.idEmailCampaign);
    }
  };

  const onCopyEmailHTML = async (): Promise<void> => {
    trackGA4SectionsUsed();
    await window.PMW.copyEmailHTML('', getEmailHTMLPromise, GA_LABEL_EXPORT_HTML, onEndExportEmail.bind(this, '.js-copy-email-html'));
  };

  const onCopyEmailHTMLAsText = async (): Promise<void> => {
    trackGA4SectionsUsed();
    await window.PMW.copyEmailHTMLasText('', getEmailHTMLPromise, GA_LABEL_EXPORT_HTML, onEndExportEmail.bind(this, '.js-copy-email-html-as-text'));
  };

  const onDownloadEmailHTML = async (): Promise<void> => {
    trackGA4SectionsUsed();
    const data = (await editor.runCommand(window.PMW.emails.commands.GET_EMAIL_HTML_PROMISE)) as EmailPreviewResponse;
    window.PMW.downloadEmailHTML(data.html, data.fileName, GA_LABEL_EXPORT_HTML, onEndExportEmail.bind(this, '.js-download-email-html'));
  };

  const setZoomPercentage = (): void => {
    const zoomValue = Math.round(window.PMW.emails.ZOOM_VALUE * 100);
    document.getElementsByClassName('zoom-val')[0].innerHTML = zoomValue.toString();
  };

  const checkZoomUpperLimit = (): boolean => {
    return window.PMW.emails.ZOOM_VALUE + window.PMW.emails.ZOOM_STEP <= window.PMW.emails.ZOOM_UPPER_LIMIT;
  };

  const checkZoomLowerLimit = (): boolean => {
    return window.PMW.emails.ZOOM_VALUE - window.PMW.emails.ZOOM_STEP >= window.PMW.emails.ZOOM_LOWER_LIMIT;
  };

  const onZoomChange = (): void => {
    showFitToScreenButton();
    showZoomLabelPreview();
    setZoomPercentage();
  };

  /**
   * Creates a selector query for the specified export HTML button state.
   *
   * @param {string} prefix
   * @param {string} state
   */
  const createButtonStateSelector = (prefix: string, state: string): string => {
    return `${prefix}-state-${state}`;
  };

  /**
   * Changes the state of the export HTML button to 'process'.
   *
   * @param {JQuery} button
   */
  const onStartExportEmail = (button: JQuery): void => {
    const selectorPrefix = getJsClassSelector(button);
    changeButtonState(selectorPrefix, BUTTON_STATE_PROCESSING);
    doKeepDropdownVisible(true);
  };

  /**
   * Changes the visibility of the export HTML dropdown.
   * @param {boolean} stayVisible
   */
  const doKeepDropdownVisible = (stayVisible: boolean): void => {
    const dropdown = $('.js-export-email-content');

    if (stayVisible) {
      dropdown.addClass('keep-open');
    } else {
      dropdown.removeClass('keep-open');
    }
  };

  /**
   * Returns the class name which is prefixed with js- of an element.
   *
   * @param {JQuery} element
   */
  const getJsClassSelector = (element: JQuery): string => {
    const classes = element.attr('class')?.split(' ');
    const jsClass = classes?.find((className) => {
      return className.startsWith('js-');
    });
    return `.${jsClass}`;
  };

  /**
   * Changes the state of the export HTML button to 'success' and then back to 'default' after timeOut seconds.
   *
   * @param {string} selectorPrefix
   * @param {number} timeOut
   */
  const onEndExportEmail = (selectorPrefix: string, timeOut = 2000): void => {
    changeButtonState(selectorPrefix, BUTTON_STATE_SUCCESS);

    setTimeout(() => {
      changeButtonState(selectorPrefix, BUTTON_STATE_DEFAULT);
      displayPremiumUpsellDialog();
      doKeepDropdownVisible(false);
    }, timeOut);
  };

  /**
   * @param {jQuery} exportBtn
   */
  const resetExportEmailUI = (exportBtn: JQuery): void => {
    const selectorPrefix = getJsClassSelector(exportBtn);
    changeButtonState(selectorPrefix, BUTTON_STATE_DEFAULT);
  };

  const saveAndGetEmailCampaignId = async (): Promise<string | undefined> => {
    let emailCampaignId: string | undefined;
    try {
      emailCampaignId = getEmailCampaignId();
    } catch (error) {
      console.error(error);
      await editor.store();

      emailCampaignId = window.PMW.idEmailCampaign!;
    }

    return emailCampaignId;
  };

  const saveAndGetEmailCampaign = async (): Promise<EmailCampaign | undefined> => {
    let emailCampaignId: string | undefined;
    try {
      emailCampaignId = getEmailCampaignId();
    } catch (error) {
      console.error(error);
      await editor.store();

      emailCampaignId = window.PMW.idEmailCampaign!;
    }

    if (emailCampaignId) {
      return getEmailMarketingCampaignById(emailCampaignId);
    }

    return undefined;
  };

  /**
   * @throws Error
   */
  const getEmailCampaignId = (): string => {
    if (window.PMW.idEmailCampaign) {
      return window.PMW.idEmailCampaign;
    }

    throw new Error('Email campaign ID does not exist. Triggering save to generate ID');
  };

  const onPublishingOptionsModalClickBack = (): void => {
    window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.EMAIL_CAMPAIGN_SENDING_OPTIONS_MODAL));
    editor.runCommand(window.PMW.emails.commands.OPEN_MORE_OPTIONS);
  };

  window.PMW.initEmailEditorPublishingOptionsDropdown(document.getElementById('email-editor-publishing-options-dropdown-container'), saveAndGetEmailCampaign);

  window.PMW.onClick($('.js-navbar-back-btn'), null, () => {
    if (window.PMW.isUserLoggedIn() && window.PMW.redirectUrlCache.getCache(window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_KEY)) {
      window.PMW.redirectUrlCache.deleteCache(window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_KEY);
    }

    window.history.back();
  });

  window.PMW.onClick($('.js-emails-nav-logo'), null, () => {
    if (window.PMW.isUserLoggedIn()) {
      redirectUser(window.PMW.util.site_url('posters/mine#/email-marketing-campaigns'), false, false);
    } else {
      redirectUser(window.PMW.util.site_url(), false, false);
    }
  });

  window.PMW.onClick($('.js-nav-email-editor-redo'), null, () => {
    editor.runCommand('core:redo');
  });

  window.PMW.onClick($('.js-nav-email-editor-undo'), null, () => {
    editor.runCommand('core:undo');
  });

  window.PMW.onClick($('.js-nav-email-editor-keyboard-shortcuts'), null, () => {
    $('.js-nav-email-editor-keyboard-shortcuts').closest('.nav-item-container').removeClass('keep-open').removeClass('selected');
    editor.runCommand(window.PMW.emails.commands.SHOW_KEYBOARD_SHORTCUTS);
  });

  window.PMW.onClick($('.js-save-email-template'), null, () => {
    $('.js-save-email-template').addClass('-loading');
    window.PMW.gtm.trackGA4CustomEvent('EmailMaker_Save');
    editor.runCommand(window.PMW.emails.commands.SAVE_TO_CACHE);
  });

  window.PMW.onClick($('.js-save-email-template-retry'), null, () => {
    window.PMW.gtm.trackGA4CustomEvent('EmailMaker_Save');
    editor.runCommand(window.PMW.emails.commands.SAVE_TO_CACHE);
  });

  window.PMW.onClick($('.js-zoom-out'), null, () => {
    if (checkZoomLowerLimit()) {
      editor.runCommand(window.PMW.emails.commands.ZOOM_OUT);
      onZoomChange();
    }
  });

  window.PMW.onClick($('.js-zoom-in'), null, () => {
    if (checkZoomUpperLimit()) {
      editor.runCommand(window.PMW.emails.commands.ZOOM_IN);
      onZoomChange();
    }
  });

  window.PMW.onClick($('.js-fit-to-screen'), null, () => {
    editor.runCommand(window.PMW.emails.commands.FIT_TO_SCREEN);
    const bottomPosterClass = $('.bottom-floating-poster-opts');
    bottomPosterClass.removeClass('show-zoom-label');
    bottomPosterClass.removeClass('show-fit-to-screen');
  });

  window.PMW.onClick(bd, '.js-copy-email-html', function (this: HTMLElement) {
    const btn = $(this);
    editor.runCommand(window.PMW.emails.commands.AUTHENTICATE_USER, {
      cb: async () => {
        onStartExportEmail(btn);
        await onCopyEmailHTML();
        editor.runCommand(window.PMW.emails.commands.EMAIL_EXPORTED, {exportOption: window.PMW.emails.exportOptions.COPY_EMAIL});
      },
      onEarlyClose: () => {
        resetExportEmailUI(btn);
      },
    });
  });

  window.PMW.onClick(bd, '.js-copy-email-html-as-text', function (this: HTMLElement) {
    const btn = $(this);
    editor.runCommand(window.PMW.emails.commands.AUTHENTICATE_USER, {
      cb: async () => {
        onStartExportEmail(btn);
        await onCopyEmailHTMLAsText();
        editor.runCommand(window.PMW.emails.commands.EMAIL_EXPORTED, {exportOption: window.PMW.emails.exportOptions.COPY_HTML});
      },
      onEarlyClose: () => {
        resetExportEmailUI(btn);
      },
    });
  });

  window.PMW.onClick(bd, '.js-download-email-html', function (this: HTMLElement) {
    const btn = $(this);
    editor.runCommand(window.PMW.emails.commands.AUTHENTICATE_USER, {
      cb: async () => {
        onStartExportEmail(btn);
        await onDownloadEmailHTML();
        editor.runCommand(window.PMW.emails.commands.EMAIL_EXPORTED, {exportOption: window.PMW.emails.exportOptions.DOWNLOAD_HTML});
      },
      onEarlyClose: () => {
        resetExportEmailUI(btn);
      },
    });
  });

  window.PMW.onClick(bd, '.js-preview-email-content', () => {
    editor.runCommand(window.PMW.emails.commands.CLOSE_PREVIEW_DROPDOWN);
    editor.runCommand(window.PMW.emails.commands.OPEN_PREVIEW_DIALOG);
  });

  window.PMW.onClick(bd, '.js-schedule-email-campaign', () => {
    window.PMW.gtm.trackGA4CustomEvent('EmailMaker_Schedule');
    editor.runCommand(window.PMW.emails.commands.CLOSE_MORE_OPTIONS);
    editor.runCommand(window.PMW.emails.commands.OPEN_CALENDAR_POPUP);
  });

  window.PMW.onClick(bd, '.js-export-email-content-mobile', () => {
    editor.runCommand(window.PMW.emails.commands.CLOSE_MORE_OPTIONS);
    editor.runCommand(window.PMW.emails.commands.OPEN_EXPORT_OPTIONS);
  });

  window.PMW.onClick(bd, '.js-toggle-email-campaign-share-in-team', async () => {
    if (!isUserPremium()) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS, noop, true);
      return;
    }

    let idEmailMarketingCampaign: string | null | undefined = window.PMW.idEmailCampaign;

    if (!window.PMW.idEmailCampaign) {
      idEmailMarketingCampaign = await saveAndGetEmailCampaignId();
    }

    if (!idEmailMarketingCampaign) {
      console.error('Error saving campaign. No ID received.');
      return;
    }

    const el = $('.js-toggle-email-campaign-share-in-team');
    const isShared = el.attr('data-is-shared') === '1';

    el.find('.js-toggle-switch').toggleClass('-active');
    el.attr('data-is-shared', isShared ? '0' : '1');

    const updatedValue = !isShared;
    await shareEmailMarketingCampaignInTeam(updatedValue, idEmailMarketingCampaign);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLAB_EMAIL_SHAREINTEAM, {
        [GA4EventParamName.TYPE]: updatedValue ? CollaborationGA4Toggle.SHARED : CollaborationGA4Toggle.UNSHARED,
        [GA4EventParamName.SOURCE]: CollaborationGA4Source.EMAIL_EDITOR,
      });
  });

  window.PMW.onClick($('.js-more-nav-options-mobile'), null, () => {
    editor.runCommand(window.PMW.emails.commands.OPEN_MORE_OPTIONS);
  });

  window.PMW.onClick(bd, '.js-duplicate-email-href', () => {
    if (window.PMW.idEmailTemplate) {
      window.open(window.PMW.util.site_url(`emailmaker/copy/${window.PMW.idEmailTemplate}`));
    } else {
      alert(window.i18next.t('pmwjs_save_and_try_again'));
    }
  });

  window.PMW.onClick(bd, '.js-share-email', async (): Promise<void> => {
    const btn = $('.js-share-email');

    if (!btn.hasClass('-active')) {
      btn.addClass('-loading');
    }

    toggleShareButtonActiveState();

    const campaign = await saveAndGetEmailCampaign();
    if (!campaign) {
      toggleShareButtonActiveState();
      btn.removeClass('-loading');
      return;
    }

    window.PMW.openShareEmailInTeamToggler(document.getElementById('email-editor-share-email-container'), campaign, (state) => {
      let isSharedInTeam = false;
      if (state === ShareInTeamBarState.ACTIVE) {
        isSharedInTeam = true;
      }

      editor.runCommand(window.PMW.emails.commands.UPDATE_SHARE_BUTTON_ICON, {isSharedInTeam: isSharedInTeam});
    }, GA4EventName.COLLAB_EMAIL_SHAREINTEAM, CollaborationGA4Source.EMAIL_EDITOR);

    btn.removeClass('-loading');
  });

  window.PMW.onClick(bd, '.js-post-to-social-media', () => {
    trackGA4SectionsUsed();
    window.PMW.openEmailEditorSocialMediaPublishingOptions(
      saveAndGetEmailCampaignId,
      EmailCampaignSendingOptionsPanelTypes.SHARE_ON_SOCIAL_MEDIA,
      onPublishingOptionsModalClickBack
    );
  });

  window.PMW.onClick(bd, '.js-publish-as-webpage', () => {
    trackGA4SectionsUsed();
    window.PMW.openEmailEditorSocialMediaPublishingOptions(saveAndGetEmailCampaignId, EmailCampaignSendingOptionsPanelTypes.SHARE_AS_WEBPAGE, onPublishingOptionsModalClickBack);
  });

  document.querySelector('.js-show-desktop-preview')?.addEventListener('click', (e) => {
    e.stopPropagation();
    $('.html-editor-canvas-container').removeClass('mobile-preview');
    editor.runCommand(window.PMW.emails.commands.SET_DEVICE_DESKTOP);
  });

  document.querySelector('.js-show-tablet-preview')?.addEventListener('click', (e) => {
    e.stopPropagation();
    editor.runCommand(window.PMW.emails.commands.SET_DEVICE_TABLET);
  });

  document.querySelector('.js-show-mobile-preview')?.addEventListener('click', (e) => {
    e.stopPropagation();
    $('.html-editor-canvas-container').addClass('mobile-preview');
    editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MOBILE);
  });

  window.PMW.onClick($('.js-publish-email-btn'), null, () => {
    trackGA4SectionsUsed();
    window.PMW.gtm.trackGA4CustomEvent('EmailMaker_Publish');
    editor.runCommand(window.PMW.emails.commands.DELETE_PREFILLED_SCHEDULE_DATE);
    editor.runCommand(window.PMW.emails.commands.SAVE_AND_PUBLISH);
  });

  const toggleShareButtonActiveState = (): void => {
    const btn = $('.js-share-email');
    btn.toggleClass('-active');
  };


  addForceCloseDropdownListener();

  editor.runCommand(window.PMW.emails.commands.UPDATE_PUBLISH_CTA_PLACEMENT);
};
